import { Component, inject } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { UsersService } from '../../services/user.service';
import { AccessibilityPreferences, AppVersion, ProjectTimeTypeShort, TimesheetUIPreferences, UIPreference, WeekDayAbbr } from '../../models/ui-preferences';
import { User } from '../../models/user';
import { CommonModule, TitleCasePipe, WeekDay } from '@angular/common';
import { BehaviorSubject, debounceTime, filter, firstValueFrom, startWith, Subscription, switchMap, tap } from 'rxjs';
import { MatFormField, MatInputModule } from '@angular/material/input';
import { RouterModule } from '@angular/router';
import { ReactiveFormsModule, FormsModule, FormControl, FormGroup, FormArray, FormBuilder } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldControl, MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { CommunityAuthService } from '../../services/community-auth.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ThemeService } from '../../../assets/theme/_theme.service';

class Permissions {
  canImpersonate: boolean = false;
  fullAccess: boolean = false;
  hasReports: boolean = false;
}

@Component({
  selector: 'app-settings',
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatCheckboxModule,
    MatOptionModule,
    ReactiveFormsModule,
    MatRadioModule,
    MatSelectModule,
    FormsModule,
    MatButtonModule,
    RouterModule,
    MatProgressSpinnerModule,
    MatCardModule]
})
export class SettingsComponent {
  userService = inject(UsersService);
  fb = inject(FormBuilder);
  auth = inject(CommunityAuthService);
  days: WeekDayAbbr[] = <WeekDayAbbr[]>"sun,mon,tue,wed,thu,fri,sat".split(",");
  currentUser?: User;
  uiPreference?: UIPreference;
  theme: ThemeService = inject(ThemeService);
  impersonatedHawkId = new FormControl();
  impersonationOptions = new BehaviorSubject<User[]>([]);
  uiPrefChangedSubject = new BehaviorSubject<UIPreference>(new UIPreference());
  general = this.fb.group({
    showTimesheetHelper: [true],
    daysOff: this.fb.array(this.days.map(d => false)),
    confirmTSSubmit: [false],
    allowTwoDigitEntry: [false],
    enableColTools: [true],
    enableRowTools: [true],
    allowPersistedTimers: [true],
    hoursRoundingMethod: ['down'],
    defaultTimesheetWeek: ['current-week']
  });
  access_and_ux = this.fb.group({
    iconBtnPersistence: [''],
    tsCellToolLocation: ['context-menu'],
    darkModePersistence: ['']
  });
  userSubscr?: Subscription;
  permissions: Permissions = new Permissions();
  loading = true;
  get sessionHideHelper() { return sessionStorage["hideHelper"] !== undefined ? sessionStorage["hideHelper"] == 'true' : undefined; }
  set sessionHideHelper(value) { sessionStorage["hideHelper"] = value; }

  constructor() {
    this.impersonatedHawkId.valueChanges.pipe(
      filter(c => c != null && typeof c === "string"),
      debounceTime(100),
      switchMap(c => this.userService.filterUsers([{ chip: null, value: c }, { chip: 'ignorestatus', value: '1' }], 10, 0))
    ).subscribe(resp => this.impersonationOptions.next(resp.users));
    const rndMeth = this.general.get('hoursRoundingMethod');
    this.general.get('allowTwoDigitEntry')?.valueChanges.pipe(startWith(false), debounceTime(50)).subscribe(v => v ? rndMeth?.enable() : rndMeth?.disable());
    this.general.get('allowTwoDigitEntry')?.valueChanges.pipe(startWith(false), debounceTime(50)).subscribe(v => this.saveUI({ timesheet: { allowTwoDigitEntry: !!v }}));
    rndMeth?.valueChanges.pipe(debounceTime(50)).subscribe(v => this.saveUI({ timesheet: { hoursRoundingMethod : <any>v ?? 'down' }}));
    this.general.get('showTimesheetHelper')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { hideHelper: !v } }));
    this.general.get('daysOff')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { daysOff: this.days.filter((_, i) => v[i]) }}));
    this.general.get('confirmTSSubmit')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { confirmTSSubmit: !!v }}));
    this.general.get('enableColTools')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { enableColTools: !!v }}));
    this.general.get('enableRowTools')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { enableRowTools: !!v }}));
    this.general.get('allowPersistedTimers')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { areTimersPersisted: !!v }}));
    this.general.get('defaultTimesheetWeek')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { defaultTimesheetWeek : <any>v ?? 'current-week' }}));
    this.access_and_ux.get('tsCellToolLocation')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ timesheet: { tsCellToolLocation : <any>v ?? 'context-menu' }}));
    this.access_and_ux.get('iconBtnPersistence')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ accessibility: { iconBtnPersistence : <any>v ?? 'transient' }}));
    this.access_and_ux.get('darkModePersistence')?.valueChanges.pipe(debounceTime(100)).subscribe(v => this.saveUI({ accessibility: { themePersistence : <any>v ?? '' }}));
    this.uiPrefChangedSubject.subscribe(uip => {
      this.uiPreference = uip;
      this.general.get('showTimesheetHelper')?.setValue(!(this.sessionHideHelper ?? uip.timesheet?.hideHelper ?? false), { emitEvent: false });
      this.general.get('allowTwoDigitEntry')?.setValue(uip.timesheet?.allowTwoDigitEntry ?? false, { emitEvent: false });
      this.general.get('confirmTSSubmit')?.setValue(uip.timesheet?.confirmTSSubmit ?? false, { emitEvent: false});
      this.general.get('enableColTools')?.setValue(uip.timesheet?.enableColTools ?? true, { emitEvent: false});
      this.general.get('enableRowTools')?.setValue(uip.timesheet?.enableRowTools ?? true, { emitEvent: false});
      this.general.get('allowPersistedTimers')?.setValue(uip.timesheet?.areTimersPersisted ?? true, { emitEvent: false});
      console.log("Default timesheet week pref:", uip.timesheet?.defaultTimesheetWeek);
      this.general.get('defaultTimesheetWeek')?.setValue(<string>(uip.timesheet?.defaultTimesheetWeek ?? "current-week"), { emitEvent: false});
      rndMeth?.setValue(uip.timesheet?.hoursRoundingMethod ?? "down", { emitEvent: false });
      uip.timesheet?.allowTwoDigitEntry ? rndMeth?.enable({ emitEvent: false}) : rndMeth?.disable({ emitEvent: false});
      const daysOff = this.days.map(d => (uip.timesheet?.daysOff ?? ['sun','sat']).includes(d) ?? false);
      this.general.get('daysOff')?.setValue(daysOff, { emitEvent: false });
      this.access_and_ux.get('tsCellToolLocation')?.setValue(uip.timesheet?.tsCellToolLocation ?? "context-menu", { emitEvent: false });
      this.access_and_ux.get('iconBtnPersistence')?.setValue(uip.accessibility?.iconBtnPersistence ?? "transient", { emitEvent: false });
      this.access_and_ux.get('darkModePersistence')?.setValue(uip.accessibility?.themePersistence ?? "", { emitEvent: false });
    })
  }

  async ngAfterViewInit() {
    this.userSubscr = this.auth.isImpersonated
      .pipe(switchMap(_ => this.userService.loadCurrentUser()))
      .subscribe(user => {
        console.log("We've swapped the user");
        const uiPreference = user.userUIPreference?.data ? JSON.parse(user.userUIPreference.data) : new UIPreference();
        // console.log("UI Preferences", uiPreference, user.userUIPreference);
        this.currentUser = user;
        const roles = user.userRoles.map(ur => ur.role.roleName.toLowerCase());
        this.permissions.canImpersonate = roles.includes("admin") || this.auth.isImpersonating || false;
        this.permissions.fullAccess = roles.includes("fullaccess");
        this.permissions.hasReports = !!user.reports.length;
        this.uiPrefChangedSubject.next(uiPreference);
        this.loading = false;
    });
  }

  async ngOnDestroy() {
    this.userSubscr?.unsubscribe();
  }

  public displayUserWith(user: User) {
    return user?.hawkId;
  }

  public async beginImpersonation() {
    const hawkId = typeof(this.impersonatedHawkId?.value) === 'string' ? this.impersonatedHawkId.value : (this.impersonatedHawkId.value as User)?.hawkId;
    if (!hawkId) return;
    const result = await firstValueFrom(this.userService.filterUsers([{ chip: 'hawkid', value: hawkId }, { chip: 'ignorestatus', value: '1' }], 1, 0));
    if (result.users.length == 0) return;
    console.log("Impersonating", result.users[0].hawkId);
    this.auth.impersonate(hawkId);
  }

  public endImpersonation() {
    this.auth.endImpersonation();
  }

  public saveUI(prefs: UIPreference) {
    console.log("Saving", prefs);
    if (!this.uiPreference || !this.currentUser) return;
    if (!this.uiPreference.timesheet) {
      this.uiPreference.timesheet = new TimesheetUIPreferences();
    }
    if (!this.uiPreference.accessibility) {
      this.uiPreference.accessibility = new AccessibilityPreferences();
    }

    if (prefs.timesheet?.confirmTSSubmit !== undefined)
      this.uiPreference.timesheet.confirmTSSubmit = prefs.timesheet.confirmTSSubmit;
    if (prefs.timesheet?.hideHelper !== undefined) {
      this.uiPreference.timesheet.hideHelper = prefs.timesheet.hideHelper ? AppVersion : false;
      delete sessionStorage["hideHelper"];
    }
    if (prefs.timesheet?.daysToHide !== undefined)
      this.uiPreference.timesheet.daysToHide = prefs.timesheet.daysToHide;
    if (prefs.timesheet?.tsCellToolLocation !== undefined)
      this.uiPreference.timesheet.tsCellToolLocation = prefs.timesheet.tsCellToolLocation;
    if (prefs.accessibility?.iconBtnPersistence !== undefined)
      this.uiPreference.accessibility!.iconBtnPersistence = prefs.accessibility.iconBtnPersistence;
    if (prefs.timesheet?.daysOff != undefined)
      this.uiPreference.timesheet.daysOff = prefs.timesheet.daysOff;
    if (prefs.timesheet?.allowTwoDigitEntry != undefined) {
      this.uiPreference.timesheet.allowTwoDigitEntry = prefs.timesheet.allowTwoDigitEntry;
    }
    if (prefs.timesheet?.defaultTimesheetWeek != undefined) {
      this.uiPreference.timesheet.defaultTimesheetWeek = prefs.timesheet.defaultTimesheetWeek;
    }
    if (prefs.timesheet?.enableColTools != undefined)
      this.uiPreference.timesheet.enableColTools = prefs.timesheet.enableColTools;
    if (prefs.timesheet?.enableRowTools != undefined)
      this.uiPreference.timesheet.enableRowTools = prefs.timesheet.enableRowTools;
    if (prefs.timesheet?.areTimersPersisted != undefined)
      this.uiPreference.timesheet.areTimersPersisted = prefs.timesheet.areTimersPersisted;
    if (prefs.timesheet?.hoursRoundingMethod != undefined) {
      this.uiPreference.timesheet.hoursRoundingMethod = prefs.timesheet.hoursRoundingMethod ?? "down";
    } if (prefs.accessibility?.themePersistence != undefined) {
      this.uiPreference.accessibility!.themePersistence = prefs.accessibility.themePersistence;
    }

    this.userService.savePreference(this.uiPreference!, this.currentUser!.id).subscribe(uipref => {
      this.uiPrefChangedSubject.next(uipref);
    });

  }
}
